import PropTypes from 'prop-types';
import { useCallback } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';

import { postRegister } from '../../lib/flotiq-client';
import {
  ResponseError,
  checkResponseStatus,
} from '../../lib/flotiq-client/response-errors';
import {
  generateRedirectUrl,
  getMaskedOrganizationName,
  getTestProps,
} from '../../lib/helpers';

import SignUpInfo from '../../components/SignUpInfo/SignUpInfo';

import RegisterForm from '../../form/RegisterForm/RegisterForm';
import Authentication from '../../hoc/Authentication/Authentication';

const Register = ({ testId = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, , { removeItem: removeUser }] = useLocalStorageState('cms.user');

  const handlePost = useCallback(
    async (values) => {
      try {
        const { body, status } = await postRegister({
          ...values,
          redirectUri: generateRedirectUrl('/register/activate'),
          plan: searchParams.has('plan') ? searchParams.get('plan') : '',
        });
        checkResponseStatus(body, status);
        toast.success(t('Global.CreatedAccount'), { duration: 10000 });

        TagManager.dataLayer({
          dataLayer: {
            event: 'sign_up',
            user_id: body.user_id,
            organization_id: body.organization_id,
            organization_name: getMaskedOrganizationName(
              body.organization_name,
            ),
            plan_id: body.plan_id,
            plan_name: body.plan_name,
            space_ids: body.space_ids,
          },
        });

        window.dataLayer.push(function () {
          this.reset();
        });

        removeUser();
        navigate('/');
        return null;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return {
            general: t('Form.CommunicationErrorMessage'),
            ...error.errors,
          };
        }
        toast.error(error.message ? error.message : t('Form.ValidationError'));
        return {
          general: t('Form.ValidationError'),
          ...error.errors,
        };
      }
    },
    [navigate, removeUser, searchParams, t],
  );

  return (
    <>
      <title>{t('Global.RegisterToFlotiq')}</title>
      <Authentication
        authButtons={true}
        headerText={t('Global.CreateAccount')}
        paragraphText={
          <>
            {t('Global.StartForFree')}{' '}
            <span className="block text-sm lg:text-base mb-1 mt-6 lg:mt-4 2xl:mt-6">
              {t('Global.HaveAccount')}
              <Link to="/login" className="text-blue pl-1">
                {t('Global.LogIn')}
              </Link>
            </span>
          </>
        }
        infoSection={<SignUpInfo />}
        {...getTestProps(testId, 'authentication', 'testId')}
      >
        <RegisterForm
          onSubmit={handlePost}
          {...getTestProps(testId, 'form', 'testId')}
        />
      </Authentication>
    </>
  );
};

export default Register;

Register.propTypes = {
  /**
   * Test id for layout
   */
  testId: PropTypes.string,
};
